import { useCallback, useEffect, useMemo, useState } from 'react'
import { useVirtualizer } from '@tanstack/react-virtual'
import { Calendar, CalendarCheck } from 'lucide-react'
import { Button, Cell } from '@highlight/ui'
import { View } from '../../components/view'
import { ViewSearchInput } from '../../components/view-search-input'
import { ViewFilterMenu } from '../../components/view-filter-menu'
import { CellListSkeleton } from '../../components/cell-list-skeleton'
import { ViewGroupLabel } from '../../components/view-group-label'
import { HistoryRow } from '../../components/history/history-row'
import { MeetingRowView } from '../../components/meeting/meeting-row'
import { MeetingPopoverRow } from '../../components/meeting/meeting-popover-row'
import { useSidepanelSurface } from '../../profile/use-sidepanel-profile'
import { useFindInPageRangeExtractor } from '../../hooks/use-find-in-page-mount-all'
import { useUpcomingViewModel } from './use-upcoming-view-model'
import { toUpcomingRows, type UpcomingRow } from './upcoming-rows'
import { PastEventRow } from './past-event-row'
import { useActiveTourTarget } from '../../components/day-zero-tour/tour-state'

const rowTourTarget = (row: UpcomingRow) =>
  row.kind === 'event' || row.kind === 'past-event' ? `meeting-${row.event.id}` : undefined

/**
 * Full-list View for upcoming meetings plus the meetings recorded earlier today.
 *
 * Every meeting is a flat row — no cards (Figma `7652:29130`) — and the one with
 * a ready brief carries it under its time line.
 */
export function UpcomingView() {
  const model = useUpcomingViewModel()
  const opensPopovers = useSidepanelSurface('upcoming') === 'rows'
  const rows = useMemo(() => toUpcomingRows(model.sections), [model.sections])
  const [scrollElement, setScrollElement] = useState<HTMLDivElement | null>(null)
  const captureScrollElement = useCallback((element: HTMLDivElement | null) => setScrollElement(element), [])
  const rangeExtractor = useFindInPageRangeExtractor()
  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollElement,
    estimateSize: (index) => {
      const row = rows[index]
      if (row?.kind === 'label') {
        return row.isFirst ? 20 : 32
      }
      if (row?.kind === 'event') {
        return row.event.brief?.conversationId ? 148 : 62
      }
      return 62
    },
    getItemKey: (index) => rows[index].key,
    overscan: 6,
    rangeExtractor,
  })

  // The list is virtualized, so a tour heading for a meeting further down has to bring its row into range.
  const tourTarget = useActiveTourTarget()
  const tourRowIndex = tourTarget ? rows.findIndex((row) => rowTourTarget(row) === tourTarget) : -1
  useEffect(() => {
    if (tourRowIndex >= 0) {
      virtualizer.scrollToIndex(tourRowIndex, { align: 'center' })
    }
  }, [tourRowIndex, virtualizer])

  return (
    <View>
      <View.Header
        icon={<Calendar />}
        title="Upcoming"
        // No options means one calendar or none, and nothing worth filtering.
        accessory={
          model.calendarOptions.length ? (
            <ViewFilterMenu
              value={model.calendarFilter}
              options={model.calendarOptions}
              subject="calendar"
              onChange={model.setCalendarFilter}
            />
          ) : undefined
        }
      >
        <ViewSearchInput
          value={model.searchQuery}
          onChange={model.setSearchQuery}
          placeholder="Search your upcoming events…"
          label="Search upcoming events"
        />
      </View.Header>

      <View.Body bottomInset="list" onScrollElement={captureScrollElement}>
        {model.isLoading ? (
          <CellListSkeleton size="default" />
        ) : rows.length === 0 ? (
          <View.Empty searchQuery={model.searchQuery}>No upcoming events</View.Empty>
        ) : scrollElement ? (
          <div className="relative w-full" style={{ height: virtualizer.getTotalSize() }}>
            {virtualizer.getVirtualItems().map((virtualRow) => {
              const row = rows[virtualRow.index]
              if (!row) {
                return null
              }
              return (
                <div
                  key={virtualRow.key}
                  data-index={virtualRow.index}
                  data-tour-section={tourTarget ? rowTourTarget(row) : undefined}
                  ref={virtualizer.measureElement}
                  className="absolute left-0 top-0 w-full pb-[2px]"
                  style={{ transform: `translateY(${virtualRow.start}px)` }}
                >
                  {row.kind === 'label' ? (
                    <ViewGroupLabel isFirst={row.isFirst}>{row.label}</ViewGroupLabel>
                  ) : row.kind === 'note' ? (
                    <HistoryRow item={row.note} onOpen={model.openMeetingNote} />
                  ) : row.kind === 'past-event' ? (
                    <PastEventRow event={row.event} calendars={model.calendars} />
                  ) : row.kind === 'event' ? (
                    opensPopovers ? (
                      <MeetingPopoverRow
                        meeting={row.event}
                        calendars={model.calendars}
                        sourceLabel="Upcoming"
                        showBriefPreview
                        isRecording={row.event.id === model.activeTranscriptEventId}
                      />
                    ) : (
                      <MeetingRowView
                        meeting={row.event}
                        calendars={model.calendars}
                        sourceLabel="Upcoming"
                        showBriefPreview
                      />
                    )
                  ) : (
                    <ClearDayRow onCreateEvent={model.createEvent} />
                  )}
                </div>
              )
            })}
          </div>
        ) : (
          <CellListSkeleton size="default" />
        )}
      </View.Body>
    </View>
  )
}

/** Same row geometry as a meeting, saying there are none left today. */
function ClearDayRow({ onCreateEvent }: { onCreateEvent: () => void }) {
  return (
    <div className="corner-smooth flex w-full items-start gap-2 rounded-xl px-3 py-2">
      <Cell.Icon>
        <CalendarCheck />
      </Cell.Icon>
      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        <p className="text-foreground type-ui-sm truncate font-normal">No more events</p>
        <span className="text-foreground-secondary type-prose-xs">The rest of your day is clear</span>
      </div>
      <Button variant="outline" onClick={onCreateEvent}>
        Create Event
      </Button>
    </div>
  )
}
